/* eslint-disable no-use-before-define */
import moment from 'moment';
import { titleize } from 'underscore.string';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import {
  getLaneDataV2,
  getShipments,
  getCarriersByMarket,
  getCarriersWithOffer,
  getCarriersByMarketV2,
  getCarriersWithOfferV2,
  getCommitmentDispatched,
} from 'utils/OfferPlannerAPIUtils';
import lodash from 'lodash';
import _ from 'underscore';
import Promise from 'bluebird';

export function getCustomerName(user = {}) {
  let name = '';

  if (user._type === 'Shipper') {
    name = user.company_name;
  } else if (user._type === 'Carrier') {
    name = user.name;
  } else if (user._type === 'Consignee') {
    name = user.company_name;
  } else if (user._type === 'Driver') {
    name = `${user.first_name} ${user.last_name}`;
  }

  return unescapeSpecialChars(`${titleize(name).trim()}`);
}

const TWO_PLUS_DAYS = moment().add(2, 'days').startOf('day');
const TOMORROW = moment().add(1, 'days').startOf('day');
const TODAY = moment().startOf('day');

const mapLaneQuery = (query) => {
  let formattedQuery = {};
  if (query && query.markets && query.markets.length) {
    formattedQuery.markets = query.markets;
  }

  if (query && query.originZone && query.originZone.length) {
    formattedQuery.originZone = query.originZone;
  }

  if (query && query.destinationZone && query.destinationZone.length) {
    formattedQuery.destinationZone = query.destinationZone;
  }
  return formattedQuery;
};

const generateLaneId = () => {
  return Math.round(Math.random() * 100000) + '';
};

const mapLanesToShipments = (lanes, drayageShipmentList) => {
  // Getting all shipment lanes
  const shipmentLanes = [];
  drayageShipmentList.forEach((shipment) => {
    const stopEquipments = shipment.stops.length
      ? lodash.flatten(shipment.stops.map((stop) => stop.equipments))
      : [];
    shipment.equipments = shipment.equipments.filter((eq) =>
      stopEquipments.includes(eq._id),
    );
    // if there doesn't already exist a lane for this shipment, add it to shipmentLanes
    if (
      !shipmentLanes.find((shipmentLane) => {
        return (
          shipmentLane.originZip === shipment.origin[0].zipcode &&
          shipmentLane.destinationZip === shipment.destination[0].zipcode
        );
      })
    ) {
      const origin = shipment.origin[0];
      const originZip = origin.zipcode;
      const originCity = origin.city;
      const destination = shipment.destination[0];
      const destinationZip = destination.zipcode;
      const destinationCity = destination.city;
      const fullOriginAddress = `${origin?.name ? origin.name + ',' : ''} ${origin?.address}`;
      const fullDestinationAddress = `${destination?.name ? destination.name + ',' : ''} ${destination?.address}`;
      const laneId = generateLaneId();
      const laneRates = [];
      shipmentLanes.push({
        laneId,
        originZip,
        originCity,
        destinationZip,
        destinationCity,
        fullOriginAddress,
        fullDestinationAddress,
        laneRates,
      });
    }
  });
  const buildLanes = lanes.reduce((acc, lane) => {
    const findLane = acc.find((shipmentLane) => {
      return (
        shipmentLane.originZip === lane.originZip &&
        shipmentLane.destinationZip === lane.destinationZip
      );
    });
    if (findLane) {
      findLane.laneRates = lane.laneRates;
    } else {
      acc.push(lane);
    }
    return acc;
  }, shipmentLanes);
  // Getting mergin
  const totalLanes = buildLanes.map((lane) => {
    const { originZip, destinationZip } = lane;
    if ((originZip, destinationZip)) {
      const foundLane = lodash.find(lanes, { originZip, destinationZip });
      if (foundLane) {
        return foundLane;
      }
      return lane;
    }
    return lane;
  });
  // Rendering all lanes
  return totalLanes.map((lane) => {
    const { originZip, destinationZip, originCity, destinationCity } = lane;
    let shipments = [];
    if (originZip && destinationZip) {
      shipments = drayageShipmentList.filter((shipment) => {
        const origin = shipment.origin[0];
        const destination = shipment.destination[0];
        if (
          origin.zipcode &&
          destination.zipcode &&
          origin.zipcode.toString() === originZip.toString() &&
          destination.zipcode.toString() === destinationZip.toString()
        ) {
          return shipment;
        }
        return false;
      });
    }
    lane.shipments = shipments;
    return lane;
  });
};

export const fetchOfferPlannerData = async (
  query,
  setPaginationPages,
  setPaginationTotal,
) => {
  const formattedQuery = mapLaneQuery(query);

  // FETCH LANE DATA
  const lanes = await getLanes(formattedQuery).catch((e) => {
    console.log(e);
    return new Error('Unable to fetch lanes');
  });

  if (!lanes || !lanes.length) {
    return { laneData: [] };
  }

  console.time('DRAYAGE SHIPMENT FETCH TIME');
  const drayageShipmentList = await getShipments(query).catch((e) => {
    console.log(e);
    return new Error('Unable to fetch shipments');
  });
  console.timeEnd('DRAYAGE SHIPMENT FETCH TIME');
  console.log('Drayage Shipment List', drayageShipmentList);

  setPaginationPages(
    drayageShipmentList.length ? drayageShipmentList[0].meta.pages : 0,
  );
  setPaginationTotal(
    drayageShipmentList.length ? drayageShipmentList[0].meta.total : 0,
  );
  const lanesWithShipments = mapLanesToShipments(
    lanes,
    drayageShipmentList.length ? drayageShipmentList[0].data : [],
  );
  const laneData = lanesWithShipments.filter((lane) => lane.shipments.length);
  const groupedData = groupByServiceType(laneData); // Splits lanes and shipments up service types
  return { laneData: groupedData };
};

const getLanes = async (query) => {
  console.time('LANE RATE FETCH TIME');

  const res = await getLaneDataV2(query).catch((e) => {
    console.log(e);
    return new Error('Unable to fetch Lane Rates');
  });

  if (!res.laneRates || !res.laneRates.length) {
    return [];
  }

  const lanes = formatLaneRateData(res.laneRates);
  console.timeEnd('LANE RATE FETCH TIME');
  console.log('Lanes', lanes);
  return lanes;
};

const groupByServiceType = (laneData) => {
  const newLanes = [];

  laneData.forEach((lane) => {
    const { shipments } = lane;

    if (!shipments.length) {
      // this is redudant
      newLanes.push(lane);
    }

    const drop = shipments.filter(
      (job) => job.order.length && job.order[0].service_type === 'drop',
    );
    const live = shipments.filter(
      (job) => job.order.length && job.order[0].service_type === 'live',
    );

    if (drop.length) {
      newLanes.push({
        ...lane,
        shipments: drop,
        service_type: 'Drop & Hook',
      });
    }

    if (live.length) {
      newLanes.push({
        ...lane,
        shipments: live,
        service_type: 'Live Load/Unload',
      });
    }
  });

  return newLanes;
};

const formatLaneRateData = (laneRates) => {
  const formattedData = [];
  const grouped = lodash.groupBy(laneRates, (item) => {
    return item.laneId;
  });

  Object.keys(grouped).forEach((key, index) => {
    const laneRates = grouped[key];

    if (laneRates.length) {
      const laneRate = laneRates[0];
      const item = {
        laneId: key,
        originCity: laneRate.originCity,
        originZip: laneRate.originZip,
        originState: laneRate.originState,
        originStreet: laneRate.originStreet,
        originZoneName: laneRate.originZoneName,
        destinationZoneName: laneRate.destinationZoneName,
        destinationState: laneRate.destinationState,
        destinationStreet: laneRate.destinationStreet,
        destinationCity: laneRate.destinationCity,
        destinationZip: laneRate.destinationZip,
        laneRates,
      };
      formattedData.push(item);
    }
  });
  return formattedData;
};

export const filterOfferPlannerList = (
  rawList,
  originZone,
  destinationZone,
  serviceType,
  marketMulti,
  lfdValueMulti,
  selectedCustomers,
  selectedOpsManagers,
) => {
  const offerPlannerList = rawList.laneData;
  let laneData = lodash.map(offerPlannerList, (lane) => {
    let _lane = lodash.cloneDeep(lane);
    if (originZone && originZone.length) {
      let foundMatch = false;
      if (_lane.originCity) {
        if (_lane.originCity.toLowerCase().includes(originZone.toLowerCase())) {
          foundMatch = true;
        }
      }
      if (_lane.originState) {
        if (
          _lane.originState.toLowerCase().includes(originZone.toLowerCase())
        ) {
          foundMatch = true;
        }
      }
      if (_lane.originStreet) {
        if (
          _lane.originStreet.toLowerCase().includes(originZone.toLowerCase())
        ) {
          foundMatch = true;
        }
      }
      if (_lane.originZip) {
        if (_lane.originZip.toLowerCase().includes(originZone.toLowerCase())) {
          foundMatch = true;
        }
      }
      if (_lane.originZoneName) {
        if (
          _lane.originZoneName.toLowerCase().includes(originZone.toLowerCase())
        ) {
          foundMatch = true;
        }
      }
      if (!foundMatch) return null;
    }
    if (destinationZone && destinationZone.length) {
      let foundMatch = false;
      if (_lane.destinationCity) {
        if (
          _lane.destinationCity
            .toLowerCase()
            .includes(destinationZone.toLowerCase())
        ) {
          foundMatch = true;
        }
      }
      if (_lane.destinationState) {
        if (
          _lane.destinationState
            .toLowerCase()
            .includes(destinationZone.toLowerCase())
        ) {
          foundMatch = true;
        }
      }
      if (_lane.destinationStreet) {
        if (
          _lane.destinationStreet
            .toLowerCase()
            .includes(destinationZone.toLowerCase())
        ) {
          foundMatch = true;
        }
      }
      if (_lane.destinationZip) {
        if (
          _lane.destinationZip
            .toLowerCase()
            .includes(destinationZone.toLowerCase())
        ) {
          foundMatch = true;
        }
      }
      if (_lane.destinationZoneName) {
        if (
          _lane.destinationZoneName
            .toLowerCase()
            .includes(destinationZone.toLowerCase())
        ) {
          foundMatch = true;
        }
      }
      if (!foundMatch) return null;
    }
    if (serviceType && serviceType.length) {
      _lane = serviceType.includes(_lane.service_type);
      if (!_lane) return null;
    }
    if (marketMulti && marketMulti.length) {
      _lane.shipments = lodash.filter(lane.shipments, (shipment) => {
        if (shipment.market && shipment.market.name) {
          return marketMulti.includes(shipment.market.name);
        }
        return false;
      });
    }
    if (lfdValueMulti && lfdValueMulti.length) {
      if (lfdValueMulti.includes('LFD Today')) {
        if (
          !_lane.last_free_date ||
          !moment(_lane.last_free_date).isSame(moment(), 'day')
        ) {
          return null;
        }
      }
      if (lfdValueMulti.includes('LFD Tomorrow')) {
        if (
          !_lane.last_free_date ||
          !moment(_lane.last_free_date).isSame(TOMORROW, 'day')
        ) {
          return null;
        }
      }
      if (lfdValueMulti.includes('Past LFD')) {
        if (
          !_lane.last_free_date ||
          !moment(_lane.last_free_date).isBefore(moment().startOf('day'))
        ) {
          return null;
        }
      }
    }
    return _lane;
  });
  laneData = lodash.compact(laneData);
  const formattedData = mapOfferPlannerData({ laneData }); // Data formatted for table
  return formattedData;
};

export const mapOfferPlannerData = (offerPlannerList) => {
  const { laneData } = offerPlannerList;

  laneData.map((lane) => {
    const grouped = lodash.groupBy(lane.shipments, (lane) => lane.shipper);
    // instead of mapping here we can use a forEach to create an object where the key is the carrier ID instead for faster lookups maybe?
    lane.shipmentsByShipper = Object.entries(grouped).map(([key, value]) => {
      const hasCarrierPreferences =
        value[0].carrier_preferences && value[0].carrier_preferences.carriers;
      const hasExcludedCarriers =
        value[0].carrier_preferences &&
        value[0].carrier_preferences.excluded_carriers;
      const preferredCarrierIds = hasCarrierPreferences
        ? value[0].carrier_preferences.carriers.map((c) => c.carrier)
        : [];
      const excludedCarrierIds = hasExcludedCarriers
        ? value[0].carrier_preferences.excluded_carriers.map((c) => c.carrier)
        : [];
      const preferredOnly = value[0].carrier_preferences
        ? value[0].carrier_preferences.preferred_only
        : false;
      const availableShipments = value.filter(
        (shipment) =>
          !shipment.carrier &&
          moment(shipment.available_date).isSameOrBefore(
            moment().startOf('day'),
          ),
      );
      return {
        _id: key,
        name: value[0].shipper_name,
        shipments: value,
        availableShipments,
        carrier_preferences: {
          preferred_only: preferredOnly,
          carriers: preferredCarrierIds,
          excluded_carriers: excludedCarrierIds,
        },
      };
    });
    return lane;
  });

  const formattedData = laneData.reduce((listLanes, lane) => {
    const { shipmentsByShipper } = lane;
    const shipperRows = shipmentsByShipper.map((carrier, carrierIndex) => {
      const { carriers, excluded_carriers, preferred_only } =
        shipmentsByShipper[carrierIndex].carrier_preferences;
      let carrierRates = lane.laneRates
        .filter(
          (lr) =>
            lr.customerType === 'carrier' &&
            !excluded_carriers.includes(carrier._id) &&
            (!preferred_only || carriers.includes(carrier._id)),
        )
        .sort((a, b) => {
          return a.rate - b.rate;
        });
      let totalCommitment = lane.laneRates.reduce(
        (previousValue, currentValue) =>
          previousValue + (currentValue.commitment || 0),
        0,
      );

      let containersNotAvailable = [];

      let containersAvailable = shipmentsByShipper[
        carrierIndex
      ].shipments.filter((job) => {
        if (job.carrier) {
          return false;
        }
        if (job.available_date) {
          if (
            moment(job.available_date).isSameOrBefore(moment().startOf('day'))
          ) {
            return true;
          }
          containersNotAvailable.push(job);
          return false;
        }
        containersNotAvailable.push(job);
        return false;
      });

      const containersDispatched = shipmentsByShipper[
        carrierIndex
      ].shipments.filter((job) => job.carrier);

      let lfd_unknown = shipmentsByShipper[carrierIndex].shipments.reduce(
        (totals, job) => {
          if (!job.last_free_date) {
            return totals.concat(job);
          }
          return totals;
        },
        [],
      );

      let lfd_today = shipmentsByShipper[carrierIndex].shipments.reduce(
        (totals, job) => {
          if (
            job.last_free_date &&
            moment(job.last_free_date).isSame(moment(), 'day')
          ) {
            return totals.concat(job);
          }
          return totals;
        },
        [],
      );

      let past_lfd = shipmentsByShipper[carrierIndex].shipments.reduce(
        (totals, job) => {
          if (
            job.last_free_date &&
            moment(job.last_free_date).isBefore(moment().startOf('day'))
          ) {
            return totals.concat(job);
          }
          return totals;
        },
        [],
      );

      let lfd_tomorrow = shipmentsByShipper[carrierIndex].shipments.reduce(
        (totals, job) => {
          if (
            job.last_free_date &&
            moment(job.last_free_date).isSame(TOMORROW, 'day')
          ) {
            return totals.concat(job);
          }
          return totals;
        },
        [],
      );

      let lfd_2days = shipmentsByShipper[carrierIndex].shipments.reduce(
        (totals, job) => {
          if (
            job.last_free_date &&
            moment(job.last_free_date).isSameOrAfter(TWO_PLUS_DAYS)
          ) {
            return totals.concat(job);
          }
          return totals;
        },
        [],
      );

      return {
        _id: lane.laneId,
        origin: lane.originZip,
        destination: lane.destinationZip,
        full_origin_address: lane?.fullOriginAddress,
        full_destination_address: lane?.fullDestinationAddress,
        type: lane.service_type,
        carriers_with_rates: [], // API call: getCarriersByMarket + carrierRates -  Use carriers in the same market to filter out carriers with rates that aren't in the market
        carrier_with_no_rates: [], // API call: getCarriersByMarket -  Carriers in the same market who do not have rates
        carrier_with_offer: 0, // External API call for this - getCarriersWithOffer(lane.shipments.map => shipment._id)
        carrier_offers: 0,
        lowest_rate: carrierRates[0]?.rate || 0,
        highest_rate: carrierRates[carrierRates.length - 1]?.rate || 0,
        total_commitment: totalCommitment,
        commitment_dispatched: 0, // External API
        containers_available: containersAvailable,
        containers_dispatched: containersDispatched || [],
        past_lfd,
        lfd_today,
        lfd_tomorrow,
        lfd_2days,
        lfd_unknown,
        containers_not_available: containersNotAvailable,
        carrierRates,
        shipments: shipmentsByShipper[carrierIndex].shipments,
        availableShipments: carrier.availableShipments,
        customer: shipmentsByShipper[carrierIndex].name,
        customerId: shipmentsByShipper[carrierIndex]._id,
        operations_manager: shipmentsByShipper[carrierIndex].operations_manager,
        carrier_preferences:
          shipmentsByShipper[carrierIndex].carrier_preferences,
      };
    });

    listLanes = listLanes.concat(shipperRows);
    return listLanes;
  }, []);
  console.log('debugger be trippin');
  return formattedData;
};

export const findCommitmentDispatched = async (offerPlannerList) => {
  if (!offerPlannerList || !offerPlannerList.length) {
    return null;
  }
  let offers = [];
  offers = await Promise.map(
    offerPlannerList,
    async (laneData) => {
      const { customerId, origin, destination } = laneData;
      if (!origin || !destination) {
        return laneData;
      }
      const res = await getCommitmentDispatched({
        shipper: customerId,
        origin,
        destination,
      }).catch((e) => {
        if (e) console.log(e);
      });
      let commitment = [];
      if (
        res &&
        res.results &&
        res.results.commitment &&
        res.results.commitment.length
      ) {
        commitment = res.results.commitment;
        laneData.commitment_dispatched = commitment;
      } else {
        laneData.commitment_dispatched = [];
      }
      return laneData;
    },
    { concurrency: 50 },
  );
  return offers;
};

function sliceIntoChunks(arr, chunkSize) {
  const res = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    res.push(chunk);
  }
  return res;
}

export const findCarriersWithOffer = async (offerPlannerList) => {
  if (!offerPlannerList || !offerPlannerList.length) {
    return null;
  }

  const lanes = lodash.map(offerPlannerList, (laneData) => {
    let shipmentIds = laneData.shipments.map((job) => job._id);
    shipmentIds = shipmentIds.sort();
    return {
      laneId: laneData._id,
      shipmentIds,
      origin: laneData.origin,
      destination: laneData.destination,
    };
  });
  const chunks = sliceIntoChunks(lanes, 20);
  let results = await Promise.map(chunks, async (lanes) => {
    const result = await getCarriersWithOfferV2({
      lanes,
    }).catch((e) => {
      if (e) console.log(e);
    });
    return result;
  });
  results = lodash.flatten(results);
  results = lodash.map(results, 'results');
  results = lodash.flatten(results);
  // const offers = await Promise.map(offerPlannerList, async (laneData) => {
  const offers = offerPlannerList.map((laneData) => {
    const { preferred_only, carriers, excluded_carriers } =
      laneData.carrier_preferences;
    const prefferredCarrier = _.find(carriers, (carrier) => {
      if (
        laneData.carrier_preferences &&
        laneData.carrier_preferences.carriers
      ) {
        return laneData.carrier_preferences.carriers.includes(carrier);
      }
      return false;
    });

    if (
      excluded_carriers.includes(laneData.customerId) ||
      (preferred_only && !prefferredCarrier)
    ) {
      laneData.carrier_with_offer = [];
      laneData.carrier_offers = [];
      return laneData;
    }
    const res = lodash.filter(
      results,
      (result) => result.laneId === laneData._id,
    );
    let foundCarrierOffers = lodash.map(res, (_res) => {
      if (_res && _res.results && _res.results.carriers) {
        return _res.results.carriers;
      }
    });
    foundCarrierOffers = lodash.flatten(foundCarrierOffers);
    foundCarrierOffers = lodash.compact(foundCarrierOffers);
    foundCarrierOffers = lodash.flatten(foundCarrierOffers);
    laneData.carrier_offers = foundCarrierOffers || [];
    laneData.carrier_with_offer = Object.values(
      lodash.groupBy(laneData.carrier_offers, (lane) => lane.carrier._id),
    ).reduce((acc, cur) => {
      acc.push({
        carrier: cur[0].carrier,
        rate: cur[0].rate,
        shipments: cur.map((offer) => offer.shipment._id),
        containersOffered: cur.length,
      });
      return acc;
    }, []);
    return laneData;
  });

  return offers;
};

export const findCarriersWithNoRates = async (offerPlannerList) => {
  if (!offerPlannerList || !offerPlannerList.length) {
    return null;
  }
  let lanes = lodash.map(offerPlannerList, (laneData) => {
    const { origin: originZip, destination: destinationZip } = laneData;
    return { laneId: laneData._id, originZip, destinationZip };
  });

  const response = await getCarriersByMarketV2({ lanes }).catch((e) => {
    if (e) console.log(e);
  });

  let results = response.results;
  const offers = offerPlannerList.map((laneData) => {
    const { origin: originZip, destination: destinationZip } = laneData;
    const res = lodash.filter(results, (result) =>
      result.lanes.includes(laneData._id),
    );
    let foundCarrierOffers = lodash.map(res, (_res) => {
      if (_res && _res.carriers) {
        return _res.carriers;
      }
    });
    foundCarrierOffers = lodash.compact(foundCarrierOffers);
    foundCarrierOffers = lodash.flatten(foundCarrierOffers);
    foundCarrierOffers = lodash.uniqBy(foundCarrierOffers, '_id');
    const marketCode = res && res.length && res[0].code ? res[0].code : '';
    laneData.carrier_with_no_rates = foundCarrierOffers || [];
    let carriersWithRate = [];
    const { preferred_only, carriers, excluded_carriers } =
      laneData.carrier_preferences;
    laneData.marketCode = marketCode;
    laneData.carrier_with_no_rates = laneData.carrier_with_no_rates.filter(
      (carrier) => {
        // if the carrier already has a rate then filter them out
        const carrierHasRates = laneData.carrierRates.find(
          (cr) => cr.customer === carrier._id,
        );
        if (carrierHasRates) {
          carriersWithRate.push(carrierHasRates);
          return false;
        }
        // if the shipper isn't set to only use preferreed carriers then allow the carrier as long as they aren't in the shipper's exclusions
        const carrierExcluded = excluded_carriers.includes(carrier._id);
        if (!preferred_only) {
          return !carrierExcluded;
        }
        // if carrier preferred only return only preferred carriers (and non-excluded carriers which probably is an unncessary check)
        const carrierPreferred = carriers.includes(carrier._id);
        return carrierPreferred && !carrierExcluded;
      },
    );
    laneData.carriers_with_rates = carriersWithRate;
    return laneData;
  });

  return offers;
};

export const getShipmentLFD = (job) => {
  const { last_free_date } = job;

  return {
    last_free_date,
    past_lfd: moment(last_free_date).isBefore(TODAY) ? last_free_date : null,
    lfd_today: moment(last_free_date).isSame(moment(), 'day')
      ? last_free_date
      : null,
    lfd_tomorrow: moment(job.last_free_date).isSame(TOMORROW, 'day')
      ? last_free_date
      : null,
    lfd_2days: moment(job.last_free_date).isSameOrAfter(TWO_PLUS_DAYS)
      ? last_free_date
      : null,
    lfd_unknown: !last_free_date ? true : null,
  };
};
